import React, { useEffect, useMemo, useState } from "react";
import { getUsersRoleCounts } from "../../../services/userServices";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// palette simple (facultative, peut être retirée si tu préfères les couleurs par défaut)
const COLORS = ["#6366F1","#22C55E","#F59E0B","#EF4444","#06B6D4","#A855F7","#0EA5E9","#84CC16"];

const RolesDistributionPanel = () => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]); // [{ role_id, role_label, count }]

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await getUsersRoleCounts();
        if (!mounted) return;
        setTotal(Number(data?.total || 0));
        setRows(Array.isArray(data?.roles) ? data.roles : []);
      } catch (e) {
        console.error(e);
        if (mounted) {
          setErr("Impossible de charger la répartition des rôles.");
          setTotal(0);
          setRows([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const chartData = useMemo(() => {
    const t = rows.reduce((acc, r) => acc + Number(r.count || 0), 0);
    return rows.map((r) => ({
      name: r.role_label,
      value: Number(r.count || 0),
      pct: t ? Math.round((Number(r.count || 0) * 1000) / t) / 10 : 0, // 1 décimale
    }));
  }, [rows]);

  return (
    <section className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-semibold">Répartition des rôles</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Total utilisateurs : <span className="font-semibold">{total.toLocaleString("fr-FR")}</span>
          </p>
        </div>
      </div>

      {err && (
        <div className="mt-3 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Donut chart */}
        <div className="h-[280px] w-full rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-2">
          {loading ? (
            <div className="h-full w-full flex items-center justify-center text-slate-500">
              Chargement…
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full w-full flex items-center justify-center text-slate-500">
              Aucune donnée
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, { payload }) => [
                    `${value} (${payload.pct}%)`,
                    payload.name,
                  ]}
                />
                <Legend layout="horizontal" verticalAlign="bottom" align="center" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Détails & KPI */}
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {chartData.map((r, i) => (
              <div
                key={r.name}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-sm"
                    style={{ background: COLORS[i % COLORS.length] }}
                  />
                  <span className="text-sm">{r.name}</span>
                </div>
                <div className="text-sm font-medium">
                  {r.value.toLocaleString("fr-FR")}{" "}
                  <span className="text-slate-500">({r.pct}%)</span>
                </div>
              </div>
            ))}
            {chartData.length === 0 && (
              <div className="text-sm text-slate-500 px-3 py-2">Aucun rôle.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RolesDistributionPanel;
